import { Container, Header, Link } from "@amzn/awsui-components-react";
import { Errors, PosturePayload } from '@amzn/siteconnect-idp-posture-assessment/dist/lib/models';
import { ReactElement, useEffect, useState } from 'react';
import * as semver from 'semver';
import * as constants from '../../../common/constants';
import { BasePageProps } from '../../../common/pageProps';
import { CodeBlockWithCopy, returnPosturePayload } from '../../../common/utils';

const defaultUpdateInstructions = (
  <ol>
    <li>
      Follow the instructions on the <Link
        href='/windows/install.html#install-siteconnect-posture-client'
      >Install Page</Link> to download and install the latest version of the SiteConnect Posture Assessment Client.
    </li>
    <li>
      Once the installer finishes, return to this page or visit the <Link
        href='/windows/troubleshooting.html'
      >Troubleshooting Page</Link> to verify the software is running.
    </li>
  </ol>);

export const UpdatingPage = ({ setPageTitle }: BasePageProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [installedVersion, setInstalledVersion] = useState<string>('Unknown');
  const [latestVersion, setLatestVersion] = useState<string>('Unknown');
  const [updateInstructions, setUpdateInstructions] = useState<ReactElement>(defaultUpdateInstructions);
  const [headerDescription, setHeaderDescription] = useState<ReactElement>(<Header variant='h2'>You are already on the latest version!</Header>);

  setPageTitle('Updating The SiteConnect Posture Assessment Client');

  useEffect(() => {
    const getPayload = async () => {
      const clientPosturePayload: PosturePayload | Errors = await returnPosturePayload();
      if ('Errors' in clientPosturePayload) {
        console.error(clientPosturePayload.Errors);
      } else {
        const wsv = clientPosturePayload.webserverversion ?? clientPosturePayload.webServerVersion;
        if (wsv) {
          setInstalledVersion(`${wsv.major}.${wsv.minor}.${wsv.patch}`);
        }
        const lwsv = clientPosturePayload.latestwebserverversion ?? clientPosturePayload.latestWebServerVersion;
        if (lwsv) {
          setLatestVersion(`${lwsv.major}.${lwsv.minor}.${lwsv.patch}`);
        }
      }

      setLoading(false);
    }

    loading && getPayload();
  }, [loading]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (installedVersion === 'Unknown' || latestVersion === 'Unknown') {
      setHeaderDescription(<Header variant='h2'>Unable to determine version information, please update your client.</Header>);
      return;
    }

    if (semver.gte(installedVersion, latestVersion)) {
      setUpdateInstructions((<p>Your client is up-to-date. No action is required.</p>));
      return;
    } else {
      setHeaderDescription(<Header variant='h2'>Update installed v{installedVersion} to latest v{latestVersion}</Header>);
    }

    if (semver.lt(latestVersion, '1.0.0')) {
      // 1.0.0 is not yet released
      if (installedVersion === 'Unknown' || semver.lt(installedVersion, '0.1.5')) {
        // installed client < 0.1.5
        return;
      } else {
        // installed client >= 0.1.5
        setUpdateInstructions(
          (
            <ol>
              <li>
                Run the following command in PowerShell as an Administrator:<br /><br />
                <CodeBlockWithCopy code={constants.updateCodeText} />
              </li>
              <li>
                Once the installer finishes, return to this page or visit the <Link
                  href='/windows/troubleshooting.html'
                >Troubleshooting Page</Link> to verify the software is running.
              </li>
            </ol>
          )
        );
      }
    } else {
      // 1.0.0 is released
      setUpdateInstructions((
        <>
          <p>Version 1.0.0 greatly simplifies the installation process, but does require a full uninstall of any previous version.</p>
          <ol>
            <li>
              <p>Uninstall the client:</p>
              <CodeBlockWithCopy code={constants.uninstallCodeText} />
            </li>
            <li>
              Click this <Link
                href="https://downloads.siteconnect.opstech.a2z.com/latest/WINDOWS/siteconnect-web-server.msi"
                variant='primary'
            >Download</Link> link to download the latest Posture Assessment Client.
            </li>
            <li>
              Run the downloaded <strong>siteconnect-web-server.msi</strong> installer as an Administrator.
            </li>
            <li>
              Proceed through the installer steps by clicking <strong>Next</strong>.
              <ul>
                <li>When prompted by <strong>Windows User Access Control</strong>, click <strong>Yes</strong> to allow the installer to make changes to your device.</li>
              </ul>
            </li>
            <li>
              Wait for the installer to complete the installation.
            </li>
            <li>
              Click <strong>Finish</strong>.
            </li>
            <li>
              Once the installer finishes, return to this page or visit the <Link
                href='/windows/troubleshooting.html'
                variant='primary'
              >Troubleshooting Page</Link> to verify the software is running.
            </li>
          </ol>
        </>
      ))
    }


  }, [installedVersion, latestVersion, loading]);

  return (
    <Container
      fitHeight
      header={<Header variant='h1' description={headerDescription} />
    }>
      {updateInstructions}
    </Container>
  );
}
